'use client';

import { VehicleSearchForm } from '@/components/app-home-page/vehicle-search-form';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Calendar, Clock, Facebook, Linkedin, Share2, Twitter } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { WarrantyPilotPost } from '~/dtos/blog';

export function BlogPostComponent({ initialPost }: { initialPost: WarrantyPilotPost | null }) {
  // Rendered from a server-provided prop so the article body is in the SSR HTML.
  const post = initialPost;

  const pathname = usePathname();
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    // Construct full URL from browser
    if (typeof window !== 'undefined') {
      setCurrentUrl(`${window.location.origin}${pathname}`);
    }
  }, [pathname]);

  const encodedUrl = encodeURIComponent(currentUrl);
  const shareText = encodeURIComponent('Check out this blog post!');

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been moved.</p>
          <Link href="/blog" className="text-[#00B67A] hover:underline flex items-center justify-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white pt-8 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto pt-20">
            <Link href="/blog" className="inline-flex items-center text-[#00B67A] hover:underline mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>

            <div className="mb-6">
              {post.categories.map((category) => {
                return (
                  <span
                    key={`${category.id}-${category.text}`}
                    className="inline-block px-3 py-1 bg-[#00B67A]/10 text-[#00B67A] text-sm font-medium rounded-full"
                  >
                    {category.text}
                  </span>
                );
              })}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{post.title}</h1>

            <div className="flex items-center mb-8">
              <div className="h-12 w-12 rounded-full overflow-hidden mr-4">
                <Image
                  src={
                    post.author.avatar ||
                    `/placeholder.svg?height=100&width=100&query=${encodeURIComponent(post.author.name) || '/placeholder.svg'}`
                  }
                  alt={post.author.name}
                  width={48}
                  height={48}
                  className="object-cover"
                />
              </div>
              <div>
                <p className="font-medium">{post.author.name}</p>
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{post.postDate}</span>
                  <span className="mx-2">•</span>
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{post.estimatedReadingTime}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="relative -mt-8">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative h-[300px] md:h-[400px] lg:h-[500px] rounded-xl overflow-hidden shadow-lg">
              <Image
                src={
                  post.featuredImage ||
                  `/placeholder.svg?height=800&width=1200&query=${encodeURIComponent(post.title)}`
                }
                alt={post.title}
                fill
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8 mb-12">
              {/* Social Share */}
              <div className="flex justify-end mb-8">
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-3">Share:</span>
                  <div className="flex space-x-2">
                    {/* Facebook */}
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Share on Facebook"
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#00B67A] hover:text-white transition-colors"
                    >
                      <Facebook className="h-4 w-4" />
                    </a>
                    {/* Twitter */}
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${shareText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Share on Twitter"
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#00B67A] hover:text-white transition-colors"
                    >
                      <Twitter className="h-4 w-4" />
                    </a>

                    {/* LinkedIn */}
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Share on LinkedIn"
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#00B67A] hover:text-white transition-colors"
                    >
                      <Linkedin className="h-4 w-4" />
                    </a>

                    {/* Copy Link */}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(currentUrl);
                        alert('Link copied to clipboard!');
                      }}
                      aria-label="Copy share link"
                      className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#00B67A] hover:text-white transition-colors"
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Article Content */}
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />

              {/* Author Bio */}
              <div className="mt-16 pt-8 border-t border-gray-200">
                <div className="flex items-start">
                  <div className="h-16 w-16 rounded-full overflow-hidden mr-6">
                    <Image
                      src={post.author.avatar}
                      alt={post.author.name}
                      width={64}
                      height={64}
                      className="object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">About {post.author.name}</h3>
                    <p className="text-gray-600">{post.author.description}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="pt-8">
              <h2 className="text-2xl font-semibold mb-6 text-center">Search Your Vehicle</h2>
              <CardContent>
                <VehicleSearchForm />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </article>
  );
}
